import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { formatApiError } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { AlertCircle, Loader2, Headphones } from 'lucide-react';

export default function RegisterPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'technician'
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Le password non corrispondono');
      return;
    }

    if (formData.password.length < 6) {
      setError('La password deve essere di almeno 6 caratteri');
      return;
    }

    setLoading(true);

    try {
      await register(formData.email, formData.password, formData.name, formData.role);
      navigate('/');
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F4F5] flex items-center justify-center p-4">
      <div className="w-full max-w-md animate-fade-up">
        {/* Logo Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#002FA7] text-white mb-4">
            <Headphones className="w-8 h-8" />
          </div>
          <h1 className="text-3xl font-medium tracking-tight text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            IT Helpdesk
          </h1>
          <p className="text-[#71717A] mt-2">Piattaforma di Assistenza Intelligente</p>
        </div>

        <Card className="border border-[#E4E4E7] shadow-none">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Registrazione
            </CardTitle>
            <CardDescription>
              Crea un nuovo account per accedere
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 p-3 text-sm text-[#DC2626] bg-red-50 border border-red-200 rounded-md" data-testid="register-error">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="name">Nome Completo</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Mario Rossi"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  data-testid="register-name-input"
                  className="border-[#E4E4E7] focus:border-[#002FA7] focus:ring-[#002FA7]"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="nome@esempio.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="register-email-input"
                  className="border-[#E4E4E7] focus:border-[#002FA7] focus:ring-[#002FA7]"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="role">Ruolo</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger data-testid="register-role-select" className="border-[#E4E4E7]" translate="no">
                    <SelectValue placeholder="Seleziona ruolo" />
                  </SelectTrigger>
                  <SelectContent translate="no">
                    <SelectItem value="technician" translate="no">Tecnico</SelectItem>
                    <SelectItem value="admin" translate="no">Amministratore</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  data-testid="register-password-input"
                  className="border-[#E4E4E7] focus:border-[#002FA7] focus:ring-[#002FA7]"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Conferma Password</Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  data-testid="register-confirm-password-input"
                  className="border-[#E4E4E7] focus:border-[#002FA7] focus:ring-[#002FA7]"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-[#002FA7] hover:bg-[#002FA7]/90 text-white font-medium"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Registrazione in corso...
                  </>
                ) : (
                  'Registrati'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm">
              <span className="text-[#71717A]">Hai già un account? </span>
              <Link to="/login" className="text-[#002FA7] hover:underline font-medium" data-testid="login-link">
                Accedi
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
